import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { FOLDER_TYPE, VIDEOFEED, VIDEO_TYPE, QUALIFIERS, CLIP, COMMENT } from '../../enums/TYPES';
import { getAllContentVideofeedsByFolderId, getAllSharedFeeds, getAllVideofeedsWithoutPagination } from '../../api/videofeeds';
import { getContentsSharedWithMe, getFolderById } from '../../api/foldersApi';
import { TClip } from '../../components/Timeline/TimelineClip';
import { STATUS, VIDEOFEED_TYPE } from '../../enums/ENUMS';
import { ACCESS_TYPE_ENUM } from '../../enums/ACCESS_ENUM';
import { SortOptions, SortType } from '../../enums/SORT';
import { API_ROUTES } from '../../enums/API_ROUTES';
import { getMyVideofeedsSortOptionAndType } from '../../helpers';

type TVideoControls = {
  playing: boolean;
  muted: boolean;
  volume: number;
  playbackRate: number;
  played: number;
  seeking: boolean;
  buffer: boolean;
  loadedSeconds: number;
  playedSeconds: number;
  loaded: number;
};

type TMarkerPosition = {
  rowIndex: number;
  clipIndex: number;
};

export type TimelineTClip = {
  id: string;
  locked_angle_id?: number | null;
  time_start: number;
  time_end: number;
  comments: COMMENT[];
  notes: { id: number; note: string }[];
  topicId: string; // this is added in the Timeline component, hack for passing parent topic id
  width: number;
  color?: string;
  clipIndex: number;
  topicName: string;
  rowIndex: number;
  isSelected?: boolean;
  qualifiers: QUALIFIERS[];
  videos?: VIDEO_TYPE[];
};

type TVideoState = {
  videoFeed: VIDEOFEED[];
  status: STATUS;
  storeSelectedClip: VIDEO_TYPE | null;
  parentFolder: FOLDER_TYPE | null;
  refreshTrigger: boolean;
  videoPlayedSeconds: number;
  videoTimelineSeconds: number;
  sortedSelectedClips: TimelineTClip[];
  videoLength: number;
  videoControls: TVideoControls;
  notes: string[][];
  qualifiers: QUALIFIERS[];
  timeLineAndComposer: string; // this should be enum not just a string
  markerPosition: TMarkerPosition;
  sortedTimelineClips: CLIP[];
  currentPlayingTimelineClip: TClip | null;
  reachedEnd: boolean;
  playThisClip: TClip | null;
};

const initialState: TVideoState = {
  videoFeed: [],
  status: STATUS.IDLE,
  storeSelectedClip: null,
  parentFolder: null,
  refreshTrigger: false,
  videoPlayedSeconds: 0,
  videoTimelineSeconds: 0,
  sortedSelectedClips: [],
  videoLength: 0,
  videoControls: {
    playing: false,
    muted: false,
    volume: 1,
    playbackRate: 1.0,
    played: 0,
    seeking: false,
    buffer: true,
    loadedSeconds: 0,
    playedSeconds: 0,
    loaded: 0,
  },
  notes: [],
  qualifiers: [],
  timeLineAndComposer: '',
  markerPosition: { rowIndex: 0, clipIndex: 0 },
  sortedTimelineClips: [],
  currentPlayingTimelineClip: null,
  reachedEnd: false,
  playThisClip: null,
};

const flattenGroupedVideofeeds = (documents: any[] = []) => {
  const flattened: any[] = [];
  const seen = new Set<string>();

  const append = (feed: any) => {
    if (!feed) return;
    const id = String(feed.id);
    if (seen.has(id)) return;
    seen.add(id);
    flattened.push(feed);
  };

  documents.forEach((feed) => {
    append(feed);
    (feed?.reference_timelines || []).forEach((timeline: any) => append(timeline));
  });

  return flattened;
};

export const getAllVideoFeedsByFolderId = createAsyncThunk(
  API_ROUTES.VIDEOFEED,
  async (data: { folderId: string; type?: VIDEOFEED_TYPE }) => {
    const localSort = getMyVideofeedsSortOptionAndType();
    if (localSort) {
      const sortAndOption = localSort.split('/');
      if (sortAndOption[0] && sortAndOption[1]) {
        const res = await getAllVideofeedsWithoutPagination(data.folderId, data?.type, sortAndOption[0] as SortType, sortAndOption[1]);
        return flattenGroupedVideofeeds(res.documents);
      }
    }

    const res = await getAllVideofeedsWithoutPagination(
      data.folderId,
      data?.type,
    );
    return flattenGroupedVideofeeds(res.documents);
  },
);

export const getAllSharedVideoFeedsByFolderId = createAsyncThunk(
  `${API_ROUTES.VIDEOFEED_SHARED_WITH_ME}/shared`,
  async (data: { folderId: string; sort: SortType; sortOption: SortOptions }) => {
    const res = await getAllSharedFeeds(data.folderId, data.sort, data.sortOption, null, 0, 1000, '', true);
    return flattenGroupedVideofeeds(res.documents);
  },
);

export const getAllContentVideofeedsThunk = createAsyncThunk(
  `${API_ROUTES.ALL_CONTENT_VIDEOFEEDS}`,
  async (data: { folderId: string; sort: SortType; sortOption: SortOptions }) => {
    const res = await getAllContentVideofeedsByFolderId(data.folderId, data.sort, data.sortOption, 0, 1000, '', undefined, true);
    return flattenGroupedVideofeeds(res.documents);
  },
);

export const getFolderByIdThunk = createAsyncThunk(`${API_ROUTES.FOLDER}folderId`, async (folderId: string) => {
  const res = await getFolderById(folderId);
  return res.data;
});

export const getSharedFolderByIdThunk = createAsyncThunk(
  `${API_ROUTES.CONTENT_SHARED_WITH_ME}folderId`,
  async ({
    folderId,
    page,
    limit,
    sort,
    search,
    sortOption,
    accessType,
  }: {
    folderId: string;
    page: number;
    limit: number;
    sort: SortType;
    search: string;
    sortOption: SortOptions;
    accessType?: ACCESS_TYPE_ENUM[];
  }) => {
    page = isNaN(page) ? 0 : page;
    limit = isNaN(limit) ? 1000 : limit;

    const res = await getContentsSharedWithMe(page, limit, sort, search, sortOption, accessType);
    const folder = res.data.documents.find((f: FOLDER_TYPE) => f.id === folderId);
    return folder;
  },
);

export const getAllContentFolderThunk = createAsyncThunk(
  `get_all_content`,
  async ({
    folderId,
    page,
    limit,
    sort,
    search,
    sortOption,
    accessType,
  }: {
    folderId: string;
    page: number;
    limit: number;
    sort: SortType;
    search: string;
    sortOption: SortOptions;
    accessType?: ACCESS_TYPE_ENUM[];
  }) => {
    page = isNaN(page) ? 0 : page;
    limit = isNaN(limit) ? 1000 : limit;
    // @ts-ignore
    const res = await getAllContentVideofeedsByFolderId(folderId, sort, sortOption, page, limit, search, accessType ? accessType[0] : null);
    const folder = {
      videofeeds: {
        ...res.documents,
      },
      id: res.documents[0].folder.id,
      name: res.documents[0].folder.name,
    };
    return folder;
  },
);

export const videoSlice = createSlice({
  name: 'videoSlice',
  initialState,
  reducers: {
    resetTimelineState: (state) => {
      state.markerPosition = {
        rowIndex: 0,
        clipIndex: 0,
      };
      state.videoControls.playing = false;
      state.sortedSelectedClips = [];
    },
    updateReachedEnd: (state, action) => {
      state.reachedEnd = action.payload;
    },
    clearVideoFeeds: (state) => {
      state.videoFeed = [];
    },
    updatePlayThisClip: (state, action: PayloadAction<TClip | null>) => {
      state.playThisClip = action.payload;
    },
    saveClipToStore: (state, action: PayloadAction<VIDEO_TYPE>) => {
      state.storeSelectedClip = action.payload;
    },
    removeStoreSelectedClip: (state) => {
      state.storeSelectedClip = null;
    },
    saveSortedTimelineClips: (state, action) => {
      state.sortedTimelineClips = action.payload;
    },
    saveCurrentPlayingTimelineClip: (state, action) => {
      state.currentPlayingTimelineClip = action.payload;
    },
    updateTimelineClipAngleLock: (state, { payload }: PayloadAction<{ clipId: string; locked_angle_id: number | null }>) => {
      const clips = [...state.sortedSelectedClips, ...state.sortedTimelineClips, state.currentPlayingTimelineClip, state.playThisClip];
      clips.forEach((clip) => {
        if (clip && String(clip.id) === String(payload.clipId)) clip.locked_angle_id = payload.locked_angle_id;
      });
    },
    triggerRefresh: (state) => {
      state.refreshTrigger = !state.refreshTrigger;
    },
    updateVideoPlayedSeconds: (state, action: PayloadAction<number>) => {
      state.videoPlayedSeconds = action.payload;
    },
    updateTimelineSeconds: (state, action: PayloadAction<number>) => {
      state.videoTimelineSeconds = action.payload;
    },
    updateMarkerPosition: (state, action) => {
      state.markerPosition.rowIndex = action.payload.rowIndex;
      state.markerPosition.clipIndex = action.payload.clipIndex;
    },
    playNextClipInOrder: (state, action?: PayloadAction<TClip>) => {
      if (action?.payload) {
        state.playThisClip = null;

        let index = state.sortedSelectedClips.findIndex((item) => item.time_start > action.payload.time_start);

        if (index < 0) {
          index = 0;
        }
        state.markerPosition.rowIndex = state.sortedSelectedClips[index].rowIndex;
        state.markerPosition.clipIndex = state.sortedSelectedClips[index].clipIndex;
        state.videoPlayedSeconds = state.sortedSelectedClips[index].time_start + 0.00001;
        state.videoControls.playedSeconds = state.sortedSelectedClips[index].time_start + 0.00001;
        state.refreshTrigger = !state.refreshTrigger;
        state.currentPlayingTimelineClip = state.sortedSelectedClips[index];
      } else {
        const currentIndex = state.sortedSelectedClips.findIndex(
          (c) => c.clipIndex === state.markerPosition.clipIndex && c.rowIndex === state.markerPosition.rowIndex,
        );
        let nextIndex = currentIndex + 1;

        if (!state.sortedSelectedClips[nextIndex]) {
          nextIndex = 0;
        }

        if (!state.reachedEnd && nextIndex === 0) {
          state.reachedEnd = true;
          state.videoControls.playing = false;
          state.videoPlayedSeconds = state.sortedSelectedClips[state.sortedSelectedClips.length - 1].time_end;
          state.videoControls.playedSeconds = state.sortedSelectedClips[state.sortedSelectedClips.length - 1].time_end;
          state.refreshTrigger = !state.refreshTrigger;
          return;
        }
        state.markerPosition.rowIndex = state.sortedSelectedClips[nextIndex].rowIndex;
        state.markerPosition.clipIndex = state.sortedSelectedClips[nextIndex].clipIndex;
        state.reachedEnd = false;
        state.videoControls.playedSeconds = state.sortedSelectedClips[nextIndex].time_start;
        state.videoPlayedSeconds = state.sortedSelectedClips[nextIndex].time_start;
        state.currentPlayingTimelineClip = state.sortedSelectedClips[nextIndex];
        state.refreshTrigger = !state.refreshTrigger;
      }
    },
    playPreviousClipInOrder: (state, action?: PayloadAction<TClip>) => {
      if (action?.payload?.id) {
        state.playThisClip = null;

        let clip = [...state.sortedSelectedClips].reverse().find((item) => item.time_start < action.payload.time_start);
        //@ts-ignore
        if (!clip || action.payload?.playLast) {
          state.markerPosition.rowIndex = state.sortedSelectedClips[state.sortedSelectedClips.length - 1].rowIndex;
          state.markerPosition.clipIndex = state.sortedSelectedClips[state.sortedSelectedClips.length - 1].clipIndex;
          state.videoPlayedSeconds = state.sortedSelectedClips[state.sortedSelectedClips.length - 1].time_start;
          state.videoControls.playedSeconds = state.sortedSelectedClips[state.sortedSelectedClips.length - 1].time_start;
          state.refreshTrigger = !state.refreshTrigger;
          state.currentPlayingTimelineClip = state.sortedSelectedClips[state.sortedSelectedClips.length - 1];
        } else {
          state.markerPosition.rowIndex = clip.rowIndex;
          state.markerPosition.clipIndex = clip.clipIndex;
          state.videoPlayedSeconds = clip.time_start;
          state.videoControls.playedSeconds = clip.time_start;
          state.refreshTrigger = !state.refreshTrigger;
          state.currentPlayingTimelineClip = clip;
        }
      } else {
        const currentIndex = state.sortedSelectedClips.findIndex(
          (c) => c.clipIndex === state.markerPosition.clipIndex && c.rowIndex === state.markerPosition.rowIndex,
        );
        let index = currentIndex - 1;

        if (!state.sortedSelectedClips[index]) {
          index = 0;
        }
        state.markerPosition.rowIndex = state.sortedSelectedClips[index].rowIndex;
        state.markerPosition.clipIndex = state.sortedSelectedClips[index].clipIndex;
        state.videoPlayedSeconds = state.sortedSelectedClips[index].time_start;
        state.videoControls.playedSeconds = state.sortedSelectedClips[index].time_start;
        state.refreshTrigger = !state.refreshTrigger;
        state.currentPlayingTimelineClip = state.sortedSelectedClips[index];
      }
    },
    jumpToThisClipInOrder: (state, action: PayloadAction<TClip>) => {
      state.markerPosition.rowIndex = action.payload.rowIndex!;
      state.markerPosition.clipIndex = action.payload.clipIndex!;
      state.videoPlayedSeconds = action.payload.time_start;
      state.videoControls.playedSeconds = action.payload.time_start;
    },
    updateSortedSelectedClips: (state, action: PayloadAction<TimelineTClip[]>) => {
      state.sortedSelectedClips = action.payload;
      state.reachedEnd = false;
    },
    saveVideoLength: (state, action: PayloadAction<number>) => {
      state.videoLength = action.payload;
    },
    updateVideoControls: (state, action: PayloadAction<TVideoControls>) => {
      state.videoControls = action.payload;
    },
    updateNotes: (state, action) => {
      state.notes = action.payload;
    },
    updateQualifiers: (state, action: PayloadAction<QUALIFIERS[]>) => {
      state.qualifiers = action.payload;
    },
    imperativeMarkerSetter: (state, action: PayloadAction<{ clipIndex: number; rowIndex: number; seconds: number }>) => {
      state.markerPosition.clipIndex = action.payload.clipIndex;
      state.markerPosition.rowIndex = action.payload.rowIndex;
      state.videoControls.playedSeconds = action.payload.seconds;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllVideoFeedsByFolderId.pending, (state) => {
        state.status = STATUS.LOADING;
      })
      .addCase(getAllVideoFeedsByFolderId.fulfilled, (state, action: PayloadAction<VIDEOFEED[]>) => {
        state.status = STATUS.IDLE;
        state.videoFeed = action.payload;
      })
      .addCase(getAllVideoFeedsByFolderId.rejected, (state) => {
        state.status = STATUS.FAILED;
      });
    builder
      .addCase(getFolderByIdThunk.pending, (state) => {
        state.status = STATUS.LOADING;
      })
      .addCase(getFolderByIdThunk.fulfilled, (state, action: PayloadAction<FOLDER_TYPE>) => {
        state.status = STATUS.IDLE;
        state.parentFolder = action.payload;
      })
      .addCase(getFolderByIdThunk.rejected, (state) => {
        state.status = STATUS.FAILED;
      });
    builder
      .addCase(getAllSharedVideoFeedsByFolderId.pending, (state) => {
        state.status = STATUS.LOADING;
      })
      .addCase(getAllSharedVideoFeedsByFolderId.fulfilled, (state, action: PayloadAction<VIDEOFEED[]>) => {
        state.status = STATUS.IDLE;
        state.videoFeed = action.payload;
      })
      .addCase(getAllSharedVideoFeedsByFolderId.rejected, (state) => {
        state.status = STATUS.FAILED;
      });
    builder
      .addCase(getAllContentVideofeedsThunk.pending, (state) => {
        state.status = STATUS.LOADING;
      })
      .addCase(getAllContentVideofeedsThunk.fulfilled, (state, action: PayloadAction<VIDEOFEED[]>) => {
        state.status = STATUS.IDLE;
        state.videoFeed = action.payload;
      })
      .addCase(getAllContentVideofeedsThunk.rejected, (state) => {
        state.status = STATUS.FAILED;
      });
    builder
      .addCase(getSharedFolderByIdThunk.pending, (state) => {
        state.status = STATUS.LOADING;
      })
      .addCase(getSharedFolderByIdThunk.fulfilled, (state, action: PayloadAction<FOLDER_TYPE>) => {
        state.status = STATUS.IDLE;
        state.parentFolder = action.payload;
      })
      .addCase(getSharedFolderByIdThunk.rejected, (state) => {
        state.status = STATUS.FAILED;
      });
    builder
      .addCase(getAllContentFolderThunk.pending, (state) => {
        state.status = STATUS.LOADING;
      })
      .addCase(getAllContentFolderThunk.fulfilled, (state, action: any) => {
        state.status = STATUS.IDLE;
        state.parentFolder = action.payload;
      })
      .addCase(getAllContentFolderThunk.rejected, (state) => {
        state.status = STATUS.FAILED;
      });
  },
});

export const {
  clearVideoFeeds,
  saveClipToStore,
  removeStoreSelectedClip,
  triggerRefresh,
  updateVideoPlayedSeconds,
  saveVideoLength,
  updateVideoControls,
  updateTimelineSeconds,
  updateNotes,
  updateQualifiers,
  updateSortedSelectedClips,
  updateMarkerPosition,
  playNextClipInOrder,
  saveSortedTimelineClips,
  saveCurrentPlayingTimelineClip,
  updateTimelineClipAngleLock,
  playPreviousClipInOrder,
  updateReachedEnd,
  imperativeMarkerSetter,
  resetTimelineState,
  jumpToThisClipInOrder,
  updatePlayThisClip,
} = videoSlice.actions;

export default videoSlice.reducer;
